import dayjs from "dayjs";

import { formatCurrencyDroppingZeroCents } from "@medbillai/utils/currency";

import type {
  CoveragePlan,
  HeadlineFigure,
  PlanSummary,
  Price,
  PriceAmount,
} from "./read/readCoverage";

export const money = (dollars: number) =>
  formatCurrencyDroppingZeroCents(dollars);

const percent = (share: number) => `${Math.round(share * 100)}%`;

const amountText = (a: PriceAmount, unit: (n: number) => string) =>
  a.kind === "one" ? unit(a.amount) : `${unit(a.min)}–${unit(a.max)}`;

const lowText = (a: PriceAmount, unit: (n: number) => string) =>
  unit(a.kind === "one" ? a.amount : a.min);

const isZero = (a: PriceAmount) => a.kind === "one" && a.amount === 0;

/** What the user pays: the copay, unless it's $0 beside a coinsurance they do pay. */
export const shownPrice = ({ copay, coinsurance }: Price) =>
  copay && !(isZero(copay) && coinsurance && !isZero(coinsurance))
    ? {
        kind: "copay" as const,
        amount: copay,
        text: amountText(copay, money),
        low: lowText(copay, money),
      }
    : coinsurance
      ? {
          kind: "coinsurance" as const,
          amount: coinsurance,
          text: amountText(coinsurance, percent),
          low: lowText(coinsurance, percent),
        }
      : null;

export const priceText = (price: Price): string =>
  shownPrice(price)?.text ?? "";

export const firstName = ({ memberName }: Pick<PlanSummary, "memberName">) =>
  memberName.split(" ")[0] || "Member";

/** Under the plan's name: the payer, unless the name already is the payer, and the year. */
export const planSubtitle = ({ planName, payerName, yearLabel }: PlanSummary) =>
  [planName ? payerName : null, yearLabel].filter(Boolean).join(" · ");

/** A Date, or a `YYYY-MM-DD` calendar date. */
export const shortDate = (date: Date | string) => dayjs(date).format("MMM D");

/** "Jan 1 – Dec 31, 2026", with the start's year only when it differs. */
export const planYearRange = ({
  start,
  end,
}: {
  start: string | null;
  end: string | null;
}) =>
  start && end
    ? `${dayjs(start).format(dayjs(start).year() === dayjs(end).year() ? "MMM D" : "MMM D, YYYY")} – ${dayjs(end).format("MMM D, YYYY")}`
    : [start, end]
        .filter(day => day !== null)
        .map(day => dayjs(day).format("MMM D, YYYY"))
        .join("");

/** A terminated plan's year can still be running: then it's just "Plan ended". */
export const planEndedText = ({ planYear, planYearEnded }: CoveragePlan) =>
  planYearEnded
    ? `Plan year ended${planYear?.end ? ` ${shortDate(planYear.end)}` : ""}`
    : "Plan ended";

export const finalFigureText = (
  { yearLabel, endedOn }: CoveragePlan,
  { kind }: HeadlineFigure,
) => {
  const figure = kind === "deductible" ? "deductible" : "out-of-pocket max";
  return endedOn
    ? `Final ${figure} · ended ${dayjs(endedOn).format("MMM YYYY")}`
    : `Final ${yearLabel ? `${yearLabel} ` : ""}${figure}`;
};

export const planYearEndedText = ({ planYear, payerName }: CoveragePlan) =>
  `The plan year ended${planYear?.end ? ` ${shortDate(planYear.end)}` : ""}. We’ll show this year’s totals once ${payerName} reports them.`;

/** A $0 deductible: nothing to pass before the plan pays more. */
export const hasNoDeductible = ({ inNetwork }: CoveragePlan) =>
  (inNetwork.deductible.individual ?? inNetwork.deductible.family)?.limit === 0;

/** A chart's start, middle and end ticks: months, or days when months would repeat. */
export const axisLabels = (start: Date, end: Date, ended: boolean) => {
  const middle = dayjs(start).add(dayjs(end).diff(start) / 2, "ms");
  const label = (format: string) => [
    dayjs(start).format(format),
    middle.format(format),
    ended ? dayjs(end).format(format) : "Today",
  ];
  const months = label("MMM");
  return new Set(months).size === months.length
    ? months
    : [...new Set(label("MMM D"))];
};
